import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook that provides requestIdleCallback with setTimeout fallback.
 * Returns [scheduleCallback, cancelCallback] tuple.
 *
 * @param timeoutMs - Maximum time to wait before forcing callback execution
 * @returns Tuple of [schedule, cancel] functions
 *
 * @example
 * const [scheduleIdle, cancelIdle] = useIdleCallback(1500);
 * scheduleIdle(() => console.log('Executed during idle time'));
 * cancelIdle(); // Cancel if needed
 */
export function useIdleCallback(timeoutMs: number): [(callback: () => void) => void, () => void] {
  const idleRef = useRef<number | ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (idleRef.current === null) {
      return;
    }
    if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
      (window as any).cancelIdleCallback(idleRef.current);
    } else {
      clearTimeout(idleRef.current as ReturnType<typeof setTimeout>);
    }
    idleRef.current = null;
  }, []);

  const schedule = useCallback(
    (callback: () => void) => {
      cancel();
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleRef.current = (window as any).requestIdleCallback(callback, { timeout: timeoutMs });
      } else {
        idleRef.current = setTimeout(callback, 0);
      }
    },
    [cancel, timeoutMs]
  );

  // Clean up on unmount
  useEffect(() => cancel, [cancel]);

  return [schedule, cancel];
}
